/* ------------------------------------------------------------------
 * quick-account.js
 *
 * Slide-in account drawer with three tabs:
 *   - Wishlist        (default open tab; one-tap → cart per item)
 *   - Enclave Rewards (Smile.io points if logged in; pitch if not)
 *   - Account         (links if logged in; inline signin/signup if not)
 *
 * Auth forms are Shopify-native (legacy customer accounts) — POST to
 * /account/login and /account on submit. Page navigates on success.
 *
 * Dual-pipe analytics matching the existing event pattern:
 *   account_drawer_open
 *   account_drawer_close
 *   account_drawer_tab_change
 *   account_drawer_link_click
 *   wishlist_to_cart_click
 *
 * Self-noops if [data-quick-account-modal] isn't on the page.
 * ------------------------------------------------------------------ */
(function () {
  if (window.__rastahQuickAccountInit) return;
  window.__rastahQuickAccountInit = true;

  var modal = document.querySelector('[data-quick-account-modal]');
  if (!modal) return;

  var scrim = modal.querySelector('[data-quick-account-scrim]');
  var closeBtn = modal.querySelector('[data-quick-account-close]');
  var tabs = modal.querySelectorAll('[data-quick-account-tab]');
  var panels = modal.querySelectorAll('[data-quick-account-panel]');
  var grid = modal.querySelector('[data-quick-account-wishlist-grid]');
  var emptyState = modal.querySelector('[data-quick-account-wishlist-empty]');

  /* Customer logged-in state — check via the customer object encoded
   * server-side in the modal's data attribute. */
  var customerAttr = modal.getAttribute('data-customer-logged-in') || '';
  var isLoggedIn = customerAttr !== '' && customerAttr !== 'null' && customerAttr !== 'false';

  var openedAt = 0;
  var pendingCloseSource = '';
  var clickedLinkInSession = false;
  var currentTab = 'wishlist';
  var wishlistHydrated = false;
  var wishlistItems = [];
  var swatRef = null;

  /* ---- Analytics ------------------------------------------------- */
  function track(event, props) {
    var tmplMatch = String(document.body.className || '').match(/template-(\S+)/);
    var payload = Object.assign({
      event: event,
      ts: Date.now(),
      page_path: window.location.pathname + window.location.search,
      page_template: tmplMatch ? tmplMatch[1] : 'unknown'
    }, props || {});
    (window.dataLayer = window.dataLayer || []).push(payload);
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(event, payload);
      }
    } catch (e) {}
  }

  /* ---- Open / Close ---------------------------------------------- */
  function open(source) {
    if (modal.classList.contains('quick-account--open')) return;
    clickedLinkInSession = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('quick-account--open');
    document.documentElement.style.overflow = 'hidden';
    openedAt = Date.now();
    /* Default tab: wishlist for BOTH login states.
     *
     * Logged-in: the Enclave tab now redirects to /account on tap
     * (per the new drawer-as-wishlist-preview UX). Defaulting to
     * 'enclave' would instant-redirect on drawer open — the user
     * would never see the wishlist. Defaulting to 'wishlist'
     * surfaces the items first and lets the user opt into the
     * dashboard via the explicit Enclave tap.
     *
     * Logged-out: same default — wishlist is the engagement entry
     * point; Enclave Rewards is the upgrade path from there. */
    var defaultTab = 'wishlist';
    setActiveTab(defaultTab, /* viaInteraction */ false);
    track('account_drawer_open', {
      is_logged_in: isLoggedIn,
      source: source || 'header_icon',
      default_tab: defaultTab
    });
    hydrateWishlist();
  }

  function close(source) {
    if (!modal.classList.contains('quick-account--open')) return;
    pendingCloseSource = source || pendingCloseSource || 'unknown';
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('quick-account--open');
    document.documentElement.style.overflow = '';
    if (openedAt) {
      track('account_drawer_close', {
        dwell_ms: Date.now() - openedAt,
        close_source: pendingCloseSource,
        clicked_link: clickedLinkInSession,
        last_tab: currentTab,
        is_logged_in: isLoggedIn
      });
    }
    openedAt = 0;
    pendingCloseSource = '';
  }

  /* ---- Tab switching --------------------------------------------- */
  function setActiveTab(tabName, viaInteraction) {
    if (tabName === currentTab && viaInteraction) return;

    // Logged-in: the Enclave tab is a deep-link to /account, NOT
    // an in-drawer panel swap. The button carries
    // `data-quick-account-tab-href`; when present + tapped, fire
    // the redirect analytic + navigate. Drawer stays open
    // momentarily so the user sees the intent (browser nav handles
    // the rest). Falls through to normal panel-swap when the attr
    // isn't set (logged-out flows still want the in-drawer rewards
    // pitch / auth panes).
    if (viaInteraction) {
      var targetTab = null;
      for (var ti = 0; ti < tabs.length; ti++) {
        if (tabs[ti].getAttribute('data-quick-account-tab') === tabName) { targetTab = tabs[ti]; break; }
      }
      var hrefAttr = targetTab && targetTab.getAttribute('data-quick-account-tab-href');
      if (hrefAttr) {
        track('account_drawer_tab_redirect', {
          from_tab: currentTab,
          to_tab: tabName,
          href: hrefAttr,
          is_logged_in: isLoggedIn
        });
        window.location.href = hrefAttr;
        return;
      }
    }

    var previousTab = currentTab;
    currentTab = tabName;

    for (var i = 0; i < tabs.length; i++) {
      var t = tabs[i];
      var isActive = t.getAttribute('data-quick-account-tab') === tabName;
      t.classList.toggle('quick-account__tab--active', isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
    for (var j = 0; j < panels.length; j++) {
      var p = panels[j];
      var isActive2 = p.getAttribute('data-quick-account-panel') === tabName;
      p.classList.toggle('quick-account__panel--active', isActive2);
      if (isActive2) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    }

    if (viaInteraction) {
      track('account_drawer_tab_change', {
        from_tab: previousTab,
        to_tab: tabName,
        is_logged_in: isLoggedIn
      });
    }
  }

  /* ---- Wishlist hydration ---------------------------------------- */
  function escape(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function formatMoney(amountMajor) {
    // Stiletto doesn't load Shopify's `option_selection.js`, so
    // `Shopify.formatMoney` is unavailable on most pages — the old
    // fallback always returned `$X.XX` regardless of the active
    // storefront currency (e.g. cart shows "Rs.108,100", wishlist
    // showed "$108100.00" — same price, two different currencies).
    //
    // Build the symbol + grouping ourselves from the active currency
    // ISO. `window.Shopify.currency.active` is set by the storefront
    // currency selector (PKR / USD / etc). Keep the prefix map in
    // sync with the symbols Stiletto's Liquid `money_format` uses
    // on the cart drawer so the wishlist and cart read identically.
    var iso = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var n = parseFloat(amountMajor);
    if (!isFinite(n)) n = 0;
    // Currencies that don't subdivide — render as whole integers.
    var zeroDecISOs = { PKR: 1, JPY: 1, KRW: 1, ISK: 1, HUF: 1, CLP: 1, IDR: 1, VND: 1 };
    var zeroDec = !!zeroDecISOs[iso];
    var digits = zeroDec ? 0 : 2;
    var formatted;
    try {
      formatted = n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    } catch (_) {
      formatted = zeroDec ? String(Math.round(n)) : n.toFixed(2);
    }
    // Prefix map mirrors what `shop.money_format` outputs on the
    // server side — keep them in lockstep across cart + wishlist.
    var prefix = {
      PKR: 'Rs.', USD: '$', CAD: 'CA$', GBP: '£', EUR: '€',
      JPY: '¥', AUD: 'A$', INR: '₹', AED: 'AED ', SAR: 'SAR '
    }[iso] || (iso + ' ');
    return prefix + formatted;
  }

  function renderCard(item) {
    var url = item.du || '#';
    var img = item.iu || '';
    var title = item.dt || 'Untitled';
    var price = item.pr;
    var vid = item.epi;
    var pid = item.empi;
    /* `va` is the variant attribute (typically size) captured by
     * wishlist.js#productPayload at heart-click time. */
    var size = item.va || '';
    /* `unrl` flags pre-release / unreleased products. Set by
     * wishlist.js#localExtras at add-time from the heart's
     * `data-wishlist-unreleased` attribute (collection has the
     * Klaviyo waitlist metafield + product is unavailable, OR
     * product is tagged unreleased/coming-soon/pre-release). When
     * truthy, the tile renders a locked state — no Move-to-Bag,
     * no size swap, "Releases soon" copy. */
    var isUnreleased = item.unrl === 1 || item.unrl === true;

    /* HTML mirrors the cart line-item structure from
     * sections/quick-cart.liquid. We REUSE the cart's class names
     * (`quick-cart__item-image`, `quick-cart__item-title-row`,
     * `quick-cart__item-title`, `quick-cart__item-price`,
     * `quick-cart__item-variant`, `quick-cart__item-bottom`,
     * `quick-cart__item-remove-x`) so wishlist cards inherit ALL
     * the existing cart typography, image proportions (4:5 cover,
     * 88px mobile / 104px desktop), padding, vertical rhythm, X
     * icon dimensions — with zero CSS duplication.
     *
     * Safe to reuse because Stiletto's cart JS scopes its bindings
     * to `.quick-cart__items` (the container — see theme.js line
     * 9255), not to `.quick-cart__item-*` class names. So our
     * wishlist tiles get the look without getting wired into the
     * cart's qty-stepper / remove handlers.
     *
     * The "MOVE TO BAG" action sits in `.quick-cart__item-bottom`
     * where the cart shows the qty stepper. Styled as a Stiletto
     * `btn btn--text-link` for visual parity with the cart's
     * existing "Remove" text-link affordance — no clunky outlined
     * icon button. */
    var closeIcon =
      '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
        '<path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" fill="none"/>' +
      '</svg>';

    // Earn-points line — mirrors the dashboard wishlist tile
    // (`Earn 330 pts`). Tier multiplier sources, in order:
    //   1. `window.__rastahTierMultiplier` — set globally in
    //      layout/theme.liquid for any logged-in page (preferred,
    //      works on PDP / collection / home / etc).
    //   2. `[data-enclave-dashboard][data-tier-multiplier]` — only
    //      present on the /account dashboard. Fallback for when
    //      theme.liquid's global wasn't emitted (legacy cached pages).
    //   3. 1 (Bronze baseline) — last resort.
    function getTierMultiplier() {
      if (typeof window.__rastahTierMultiplier === 'number' && window.__rastahTierMultiplier > 0) {
        return window.__rastahTierMultiplier;
      }
      var root = document.querySelector('[data-enclave-dashboard]');
      if (!root) return 1;
      return parseFloat(root.getAttribute('data-tier-multiplier')) || 1;
    }
    var earnPts = (isLoggedIn && price != null)
      ? Math.floor((price || 0) * getTierMultiplier())
      : null;
    var earnHTML = (earnPts != null && earnPts > 0)
      ? '<p class="account__wishlist-card-earn">Earn ' + earnPts.toLocaleString('en-US') + ' pts' + (isUnreleased ? ' on release' : '') + '</p>'
      : '';

    // Pre-release tile variant: no Move-to-Bag (product isn't
    // purchasable), no size swap (variants may not exist yet on
    // a draft listing), "Releases soon" lock chip in place of the
    // size row. Remove button + image + title + price all remain
    // so the user can still curate the list.
    var middleHTML = isUnreleased
      ? '<span class="quick-account__wl-unreleased">' +
          '<svg width="11" height="11" viewBox="0 0 11 11" aria-hidden="true" style="vertical-align:-1px;margin-right:4px"><path d="M3 5V3.5a2.5 2.5 0 0 1 5 0V5M2 5h7v5H2z" fill="none" stroke="currentColor" stroke-width="1"/></svg>' +
          'Releases soon' +
        '</span>' + earnHTML
      : '<button type="button" class="account__wishlist-card-size quick-account__wl-size" data-quick-account-size data-product-url="' + escape(url) + '">' +
          '<span data-quick-account-size-label>' + (size ? escape(size) : 'Select size') + '</span>' +
          ' <span class="account__wishlist-card-size-chev" aria-hidden="true">▾</span>' +
        '</button>' + earnHTML;

    // Move-to-Bag button class swaps based on login: logged-in users
    // get the dashboard's `account__wishlist-card-move` (cream-on-
    // dark filled CTA) for visual parity with /account. Logged-out
    // users keep the Stiletto `btn--text-link` treatment because the
    // drawer is on a cream sheet and the dashboard CTA would clash.
    var moveBtnClasses = isLoggedIn
      ? 'account__wishlist-card-move'
      : 'btn btn--text-link quick-account__wl-move';

    var bottomHTML = isUnreleased
      ? '<span class="quick-account__wl-locked-msg fs-body-50">We’ll notify you when live</span>' +
        '<button type="button" class="quick-cart__item-remove-x" data-quick-account-remove aria-label="Remove from wishlist">' +
          closeIcon +
        '</button>'
      : '<button type="button" class="' + moveBtnClasses + '" data-quick-account-add-to-cart>' +
          'Move to bag' +
        '</button>' +
        '<button type="button" class="quick-cart__item-remove-x" data-quick-account-remove aria-label="Remove from wishlist">' +
          closeIcon +
        '</button>';

    // Logged-in users get the same dark Enclave card treatment
    // the /account dashboard uses — `account__wishlist-card` +
    // `account__wishlist-card--enclave` so existing CSS rules
    // (cream text, dark background per panel override, hover
    // states) light up without per-tile work here.
    var classes = 'quick-cart__item quick-account__wl-item';
    if (isLoggedIn) classes += ' account__wishlist-card account__wishlist-card--enclave';
    if (isUnreleased) classes += ' quick-account__wl-item--unreleased';

    return '' +
      '<div class="' + classes + '" data-pid="' + escape(pid) + '" data-vid="' + escape(vid) + '"' + (isUnreleased ? ' data-unreleased="true"' : '') + '>' +
        '<div class="quick-cart__item-image">' +
          '<a href="' + escape(url) + '" class="quick-cart__item-link">' +
            (img
              ? '<img class="image" src="' + escape(img) + '" alt="' + escape(title) + '" loading="lazy">'
              : '<div class="image image--placeholder"></div>') +
          '</a>' +
        '</div>' +
        '<div class="quick-cart__item-details">' +
          '<div class="quick-cart__item-top">' +
            '<div class="quick-cart__item-title-row">' +
              '<h4 class="quick-cart__item-title fs-product-card-title">' +
                '<a href="' + escape(url) + '">' + escape(title) + '</a>' +
              '</h4>' +
              (price != null
                ? '<div class="quick-cart__item-price-wrapper fs-body-75">' +
                    '<span class="quick-cart__item-price">' + formatMoney(price) + '</span>' +
                  '</div>'
                : '') +
            '</div>' +
          '</div>' +
          '<div class="quick-cart__item-middle">' + middleHTML + '</div>' +
          '<div class="quick-cart__item-bottom">' + bottomHTML + '</div>' +
        '</div>' +
      '</div>';
  }

  /* Toggle `is-empty` class on the wishlist panel — CSS uses this
   * to hide the bottom auth-CTA footer when the wishlist is empty.
   * The user shouldn't see a signin pitch on an empty wishlist —
   * there's nothing to "save across devices" yet. Only when the
   * wishlist has items do we surface the dual Sign in / Join CTAs. */
  function setEmptyState(isEmpty) {
    var panel = modal.querySelector('[data-quick-account-panel="wishlist"]');
    if (!panel) return;
    panel.classList.toggle('quick-account__panel--wishlist-empty', isEmpty);
  }

  function showEmpty() {
    setEmptyState(true);
    if (!grid) return;
    if (emptyState) {
      grid.innerHTML = '';
      grid.appendChild(emptyState);
      emptyState.removeAttribute('hidden');
    } else {
      /* Fallback markup — mirrors the section's Liquid template
       * with the same Stiletto `quick-cart__empty-state*` classes
       * so styling stays consistent regardless of which branch
       * renders the empty state. Copy + CTA are read from the
       * grid's data-* attributes (set from theme-editor settings in
       * the Liquid) so this path honours the merchant's customised
       * text too — falling back to the stock strings if absent. */
      var esc = function (s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      };
      var eHeading = (grid.getAttribute('data-empty-heading') || 'Your wishlist is empty');
      var eText = (grid.getAttribute('data-empty-text') || 'Save items you love so you can come back to them.');
      var eCtaLabel = (grid.getAttribute('data-empty-cta-label') || 'Explore new arrivals');
      var eCtaUrl = (grid.getAttribute('data-empty-cta-url') || '/collections/all');
      grid.innerHTML = '<div class="quick-cart__empty-state quick-account__wishlist-empty"><h3 class="quick-cart__empty-state-heading ff-heading fs-heading fs-heading-4-base">' + esc(eHeading) + '</h3><p class="quick-cart__empty-state-promot-text rte ff-body fs-body-100">' + esc(eText) + '</p><div class="quick-cart__empty-state-promo-button"><a class="btn btn--medium btn--secondary" href="' + esc(eCtaUrl) + '">' + esc(eCtaLabel) + '</a></div></div>';
    }
  }

  function renderWishlist(items) {
    wishlistItems = items || [];
    if (!grid) return;
    if (!wishlistItems.length) {
      showEmpty();
      return;
    }
    setEmptyState(false);
    grid.innerHTML = wishlistItems.map(renderCard).join('');
  }

  /* Wishlist hydration reads from the localStorage mirror maintained
   * by assets/wishlist.js. Items appear in the drawer instantly,
   * no network round-trip. The mirror is updated on every add/remove
   * via wishlist.js → so the drawer always reflects the latest local
   * state. Server-side total count comes from Swym (the header badge)
   * — if local < server, we show a hint that more items exist on
   * other devices. */
  function getLocalItems() {
    if (window.RastahWishlist && typeof window.RastahWishlist.getLocalItems === 'function') {
      return window.RastahWishlist.getLocalItems();
    }
    try {
      var raw = window.localStorage.getItem('rastah_wishlist_items_v1');
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function hydrateWishlist() {
    /* Get Swym swat reference for remove ops + keep it cached */
    if (!swatRef && window.SwymCallbacks) {
      window.SwymCallbacks.push(function (swat) { swatRef = swat; });
    }
    var items = getLocalItems();
    renderWishlist(items);
    wishlistHydrated = true;
  }

  /* Re-render the drawer whenever the local wishlist mirror updates
   * (fired from wishlist.js after every add/remove). This way if the
   * drawer is already open and the user clicks a heart on a PDP, the
   * drawer's contents update in real time. */
  document.addEventListener('rastah:wishlist:local-updated', function () {
    if (currentTab === 'wishlist') {
      renderWishlist(getLocalItems());
    }
  });

  /* ---- One-tap add-to-cart from wishlist ------------------------- *
   *
   * UX contract:
   *   1. ATC adds the wishlist item's variant to the cart
   *   2. Remove the item from the wishlist (Swym + local mirror)
   *      — once it's in the cart, it shouldn't keep cluttering the
   *      wishlist (matches the conventional move-to-bag pattern)
   *   3. Close the quick-account drawer
   *   4. Open the Stiletto quick-cart drawer so the user immediately
   *      sees the new item land in their bag
   *   5. Refresh the Stiletto cart drawer's contents via
   *      `quick-cart:updated` (which triggers refreshQuickCart() in
   *      theme.js line 9351 — re-pulls /cart?section_id=quick-cart
   *      and swaps the inner container so the new line item is
   *      visible, not just the count) */
  /* Refresh the cart-drawer DOM by re-fetching its section HTML
   * and swapping the inner container.
   *
   * Why this approach: Stiletto's `quick-cart:updated` listener
   * (theme.js line 9283 — `c("quick-cart:updated", ...)`) is bound
   * to its INTERNAL PubSub channel, not document events. Dispatching
   * `new CustomEvent('quick-cart:updated')` does NOT reach it. That's
   * why the count badge updated (Stiletto polls /cart.js for that)
   * but the line items inside the drawer never refreshed.
   *
   * Solution: do the section refresh ourselves. Hit
   * /cart?section_id=quick-cart, take the response, parse out the
   * .quick-cart__container we care about, and swap its innerHTML
   * into the live DOM. Same thing Stiletto's `refreshQuickCart`
   * does under the hood. */
  function refreshStilettoCart() {
    return fetch('/cart?section_id=quick-cart', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var freshContainer = holder.querySelector('.quick-cart__container');
        var liveContainer = document.querySelector('.quick-cart .quick-cart__container');
        if (freshContainer && liveContainer) {
          liveContainer.innerHTML = freshContainer.innerHTML;
        }
      })
      .catch(function (err) {
        console.warn('[quick-account] cart refresh failed', err);
      });
  }

  function openStilettoCart() {
    /* Stiletto's quick-cart opens via the cart icon's click handler
     * (theme.js line 8755 — `r$1("quick-cart:open")` is the internal
     * PubSub call we can't access directly from outside the bundle).
     * Simulating a click on the cart icon is the supported public
     * pathway. */
    var cartIcon = document.querySelector('[data-js-cart-icon]');
    if (cartIcon) cartIcon.click();
  }

  function removeFromWishlistAfterAtc(card, pid, vid) {
    /* Pull the item record so we can pass it to Swym */
    var item = wishlistItems.find(function (it) { return it.empi === pid && it.epi === vid; });

    /* Local mirror cleanup — runs unconditionally so the heart
     * un-fills and the badge decrements even if Swym is slow / down. */
    try {
      var raw = window.localStorage.getItem('rastah_wishlist_items_v1');
      var arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr)) {
        arr = arr.filter(function (i) { return !(i.empi === pid && i.epi === vid); });
        window.localStorage.setItem('rastah_wishlist_items_v1', JSON.stringify(arr));
        document.dispatchEvent(new CustomEvent('rastah:wishlist:local-updated', { detail: { count: arr.length } }));
      }
    } catch (e) {}
    wishlistItems = wishlistItems.filter(function (it) { return !(it.empi === pid && it.epi === vid); });

    /* Best-effort Swym sync — silent failure is fine here since the
     * user's primary intent (add to cart) has already succeeded. */
    var swat = swatRef || window._swat;
    if (item && swat && typeof swat.removeFromWishList === 'function') {
      swat.removeFromWishList(item, function () {}, function () {});
    }
  }

  function addToCart(card) {
    var vid = parseInt(card.getAttribute('data-vid'), 10);
    var pid = parseInt(card.getAttribute('data-pid'), 10);
    var atcBtn = card.querySelector('[data-quick-account-add-to-cart]');
    if (!vid || !atcBtn) return;
    // Defense in depth: unreleased tiles don't render the ATC
    // button, but if a DOM hot-edit / stale handler ever fires this
    // for a locked item, bail before hitting /cart/add.js.
    if (card.getAttribute('data-unreleased') === 'true') return;

    /* Text-link button — swap label for loading + success feedback. */
    var originalLabel = atcBtn.textContent;
    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: vid, quantity: 1 })
    })
    .then(function (r) { return r.json().then(function (body) { return { ok: r.ok, body: body }; }); })
    .then(function (res) {
      if (!res.ok) {
        atcBtn.disabled = false;
        atcBtn.textContent = (res.body && res.body.description ? 'Sold out' : originalLabel);
        track('wishlist_to_cart_click', {
          product_id: String(pid), variant_id: String(vid),
          success: false, error: (res.body && res.body.description) || 'unknown',
          source: 'quick_account_drawer'
        });
        setTimeout(function () { atcBtn.textContent = originalLabel; }, 1800);
        return;
      }
      atcBtn.textContent = 'Added ✓';

      track('wishlist_to_cart_click', {
        product_id: String(pid), variant_id: String(vid),
        success: true, source: 'quick_account_drawer'
      });
      if (res.body && res.body.title) {
        track('wishlist_to_cart_success', {
          product_handle: res.body.handle,
          product_title: res.body.title,
          variant_id: String(vid),
          price: res.body.price,
          source: 'quick_account_drawer'
        });
      }

      /* 1. Remove from wishlist (Swym + local mirror + dispatch event
       *    that wishlist.js listens to for heart un-fill on PDP) */
      removeFromWishlistAfterAtc(card, pid, vid);

      /* 2. Reconcile free-gift-with-purchase. This one-tap add bypasses
       *    Stiletto's cart pipeline, so theme.min.js's syncGiftProducts()
       *    never fires and a newly-qualifying gift would be missing. Add
       *    it first, then refresh so the drawer + header count include it.
       *    Guarded + non-throwing: if the helper is absent or errors we
       *    still proceed with the normal refresh. */
      (window.rastahSyncFreeGifts ? window.rastahSyncFreeGifts() : Promise.resolve()).then(function () {
        /* 2a. Update header cart count (now reflects any gift). */
        fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (rr) { return rr.json(); })
          .then(function (cart) {
            var newCount = cart.item_count || 0;
            var countEls = document.querySelectorAll('[data-js-cart-count]');
            for (var i = 0; i < countEls.length; i++) countEls[i].textContent = newCount;
            document.dispatchEvent(new CustomEvent('theme:cart:updated', { detail: { source: 'wishlist_one_tap', cart: cart } }));
          })
          .catch(function () {});

        /* 2b. Refresh the cart drawer DOM, THEN close this drawer and
         *     open the cart drawer. Chaining via .then() guarantees the
         *     new line item (and gift) is in the DOM before the cart
         *     slides open — otherwise the user sees an empty drawer
         *     flash before the section swap lands. */
        refreshStilettoCart().then(function () {
          /* Re-run the free-shipping / gift progress bar on the freshly
           * swapped-in drawer. The manual section swap injects a fresh
           * but UNFILLED bar (Stiletto's own refresh is behind an
           * internal pubsub we can't reach), so without this the bar
           * stays hidden/empty after a wishlist add. */
          var fsbBar = document.querySelector('.quick-cart [data-free-shipping-bar]');
          if (fsbBar && window.rastahRefreshFreeShippingBar) window.rastahRefreshFreeShippingBar(fsbBar);
          close('atc_to_cart');
          openStilettoCart();
        });
      });
    })
    .catch(function (err) {
      atcBtn.disabled = false;
      atcBtn.textContent = originalText;
      console.warn('[quick-account] add-to-cart failed', err);
    });
  }

  function removeFromWishlist(card) {
    var pid = parseInt(card.getAttribute('data-pid'), 10);
    var vid = parseInt(card.getAttribute('data-vid'), 10);
    var item = wishlistItems.find(function (it) { return it.empi === pid && it.epi === vid; });
    if (!item) return;
    card.style.opacity = '0.4';

    /* Fallback to window._swat (the global Swym ref) if our cached
     * swatRef wasn't populated yet by the SwymCallbacks queue. */
    if (!swatRef && window._swat && typeof window._swat.removeFromWishList === 'function') {
      swatRef = window._swat;
    }

    function localCleanup() {
      /* Update the LS mirror so other surfaces (header badge,
       * PDP hearts on next page load) reflect the removal.
       *
       * The dispatch fires `rastah:wishlist:local-updated` which
       * wishlist.js listens to — that handler updates the badge to
       * the new local count. We do NOT call fetchWishlistCount
       * directly here anymore: Swym's server has propagation lag,
       * and querying it right after a remove can return the stale
       * pre-remove count, which would then overwrite the correct
       * local count on the badge. The earlier "empty wishlist shows
       * 2" bug was caused by this exact race. */
      try {
        var raw = window.localStorage.getItem('rastah_wishlist_items_v1');
        var arr = raw ? JSON.parse(raw) : [];
        if (Array.isArray(arr)) {
          arr = arr.filter(function (i) { return !(i.empi === pid && i.epi === vid); });
          window.localStorage.setItem('rastah_wishlist_items_v1', JSON.stringify(arr));
          document.dispatchEvent(new CustomEvent('rastah:wishlist:local-updated', { detail: { count: arr.length } }));
        }
      } catch (e) {}
      wishlistItems = wishlistItems.filter(function (it) { return !(it.empi === pid && it.epi === vid); });
      card.remove();
      if (!wishlistItems.length) showEmpty();
      track('wishlist_remove', {
        product_id: String(pid), variant_id: String(vid),
        source: 'quick_account_drawer', is_anonymous: !isLoggedIn
      });
    }

    if (!swatRef) {
      /* Swym not loaded — best-effort local removal. */
      localCleanup();
      return;
    }
    /* Swym uses (success, error) two-callback signature, not (err, data) */
    swatRef.removeFromWishList(item, localCleanup, function onError() {
      card.style.opacity = '1';
    });
  }

  /* ---- Auth pane switching (join / signin / signup) -------------- *
   * The Enclave hero's title text morphs to match the active pane.
   * Default state lands on "The Enclave" — the brand name as
   * statement. When the user commits to joining, the cursive
   * becomes "Join The Enclave" — directive, decisive.
   *
   * On the JOIN pane the cursive hero is HIDDEN entirely (same as
   * signup) because the carousel slides have their OWN cursive
   * headlines and a competing brand hero would crowd them. The
   * shared "hide the cursive hero" class is `--signup-mode` and
   * gets applied for both join and signup. */
  var HERO_TITLES = {
    signin: 'The Enclave',
    signup: 'Join The Enclave',
    join: 'The Enclave'
  };

  function setAuthPane(paneName) {
    /* Toggle the form panels */
    var panes = modal.querySelectorAll('[data-quick-account-auth-pane]');
    for (var i = 0; i < panes.length; i++) {
      var p = panes[i];
      var active = p.getAttribute('data-quick-account-auth-pane') === paneName;
      p.classList.toggle('quick-account__auth-pane--active', active);
      if (active) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    }
    /* Update the hero's cursive title to match the active pane.
     * (Mostly inert in signup/join mode since the cursive hero is
     * hidden by CSS — but kept for safety in case the card hero or
     * carousel ever fails to render.) */
    var titleEl = modal.querySelector('[data-quick-account-enclave-title]');
    if (titleEl && HERO_TITLES[paneName]) {
      titleEl.textContent = HERO_TITLES[paneName];
    }
    /* Toggle the signup-mode class on the enclave container so CSS
     * can swap heroes:
     *   signin → cursive "The Enclave"
     *   signup → embossed Rastah Card (cursive hidden)
     *   join   → carousel slides (cursive hidden)
     * Both signup and join hide the cursive hero, so we apply the
     * same class for both. */
    var enclaveContainer = modal.querySelector('[data-quick-account-enclave]');
    if (enclaveContainer) {
      var hideHero = (paneName === 'signup' || paneName === 'join');
      enclaveContainer.classList.toggle('quick-account__enclave--signup-mode', hideHero);
    }
    /* When the join pane is (re)activated, reset the carousel to
     * slide 0. Otherwise re-opening the drawer after the user had
     * scrolled to slide 3 leaves them on slide 3 — confusing as the
     * dots indicator would re-render starting from slide 1. */
    if (paneName === 'join') {
      resetJoinCarousel();
    }
  }

  /* ---- Rastah Card live name emboss --------------------------- *
   * When the user types into the First Name field on the signup
   * pane, the value is mirrored onto the loyalty card's name slot
   * in real time. Same trick Represent uses on their Prestige card.
   * Last name is included too so the full embossed name shows. */
  function bindCardNameEmboss() {
    var firstNameInput = modal.querySelector('[data-rastah-card-first-name]');
    var lastNameInput = modal.querySelector('[data-rastah-card-last-name]');
    var cardName = modal.querySelector('[data-rastah-card-name]');
    if (!cardName) return;

    function updateCardName() {
      var first = firstNameInput ? firstNameInput.value.trim() : '';
      var last = lastNameInput ? lastNameInput.value.trim() : '';
      var combined = (first + ' ' + last).trim();
      /* Truncate to ~22 chars to fit the card's name slot — longer
       * names ellipsize via CSS but capping here also prevents
       * absurd visual edge cases. */
      if (combined.length > 22) combined = combined.slice(0, 22) + '…';
      cardName.textContent = combined;
    }

    if (firstNameInput) firstNameInput.addEventListener('input', updateCardName);
    if (lastNameInput) lastNameInput.addEventListener('input', updateCardName);
  }
  bindCardNameEmboss();

  /* ---- Join carousel (pre-signup value-prop slides) ------------- *
   * Three swipeable slides above the JOIN THE ENCLAVE CTA. Native
   * horizontal touch scroll + CSS scroll-snap; we just sync the
   * dots indicator to whatever slide is currently centered.
   *
   * IntersectionObserver watches each slide against the slides-track
   * viewport. The slide with the highest intersection ratio wins
   * the active-dot state. This handles swipes, mouse drags, AND
   * programmatic scrollTo (dot taps) with a single observer.
   *
   * No auto-advance — swipe is the only interaction. Auto-rotation
   * on a 3-slide signup pitch hurts more than it helps (users want
   * to read the body copy; advancing under them is hostile).
   *
   * `resetJoinCarousel()` is exposed for `setAuthPane('join')` to
   * snap the carousel back to slide 0 when the user re-enters the
   * pane (e.g. via the wishlist footer's Join shortcut after they
   * had scrolled deep). */
  var joinSlidesEl = null;
  var joinSlideEls = [];
  var joinDotEls = [];
  var joinObserver = null;

  function setActiveJoinDot(index) {
    if (!joinDotEls.length) return;
    for (var i = 0; i < joinDotEls.length; i++) {
      var isActive = i === index;
      joinDotEls[i].classList.toggle('quick-account__join-dot--active', isActive);
      joinDotEls[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  function resetJoinCarousel() {
    if (!joinSlidesEl) return;
    /* `scrollTo({left: 0})` instead of `scrollLeft = 0` so we can
     * skip the smooth-scroll easing — we want this to snap. */
    joinSlidesEl.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    setActiveJoinDot(0);
  }

  function bindJoinCarousel() {
    joinSlidesEl = modal.querySelector('[data-quick-account-join-slides]');
    if (!joinSlidesEl) return;
    joinSlideEls = Array.prototype.slice.call(
      joinSlidesEl.querySelectorAll('[data-quick-account-join-slide]')
    );
    joinDotEls = Array.prototype.slice.call(
      modal.querySelectorAll('[data-quick-account-join-dot]')
    );
    if (!joinSlideEls.length || !joinDotEls.length) return;

    /* Dot tap → scroll the matching slide into view. CSS scroll-snap
     * handles the alignment; we just request the scroll. `behavior:
     * 'smooth'` respects `prefers-reduced-motion` natively (browsers
     * downgrade to 'auto' when the user opts out of motion). */
    for (var d = 0; d < joinDotEls.length; d++) {
      (function (idx) {
        joinDotEls[idx].addEventListener('click', function () {
          var target = joinSlideEls[idx];
          if (!target) return;
          joinSlidesEl.scrollTo({
            left: target.offsetLeft,
            top: 0,
            behavior: 'smooth'
          });
          /* Set immediately for perceived snap — observer will
           * re-affirm once the scroll settles. */
          setActiveJoinDot(idx);
        });
      })(d);
    }

    /* IntersectionObserver — fire when a slide is at least 60% in
     * view of the slides-track. Threshold tuned so a partially-
     * dragged slide doesn't flip the dot prematurely; the dot only
     * advances once the next slide is the dominant occupant. */
    if (typeof IntersectionObserver === 'function') {
      joinObserver = new IntersectionObserver(function (entries) {
        /* Pick the entry with the highest intersection ratio that's
         * currently intersecting. Iterating all entries (not just
         * the last) handles the case where two slides cross the
         * threshold in the same frame during a fast swipe. */
        var best = null;
        for (var i = 0; i < entries.length; i++) {
          var e = entries[i];
          if (!e.isIntersecting) continue;
          if (!best || e.intersectionRatio > best.intersectionRatio) {
            best = e;
          }
        }
        if (!best) return;
        var idx = parseInt(best.target.getAttribute('data-quick-account-join-slide'), 10);
        if (!isNaN(idx)) setActiveJoinDot(idx);
      }, {
        root: joinSlidesEl,
        threshold: [0.6, 0.9]
      });
      for (var s = 0; s < joinSlideEls.length; s++) {
        joinObserver.observe(joinSlideEls[s]);
      }
    } else {
      /* Fallback for ancient browsers — scroll listener with
       * round-to-nearest-slide math. */
      joinSlidesEl.addEventListener('scroll', function () {
        var slideWidth = joinSlidesEl.clientWidth;
        if (!slideWidth) return;
        var idx = Math.round(joinSlidesEl.scrollLeft / slideWidth);
        if (idx >= 0 && idx < joinSlideEls.length) setActiveJoinDot(idx);
      }, { passive: true });
    }
  }
  bindJoinCarousel();

  /* ---- Event delegation ------------------------------------------ */
  document.addEventListener('click', function (e) {
    /* Trigger from header */
    var trigger = e.target.closest && e.target.closest('[data-quick-account-trigger]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      open('header_icon');
      return;
    }

    if (!modal.classList.contains('quick-account--open')) return;

    /* Tab clicks */
    var tab = e.target.closest('[data-quick-account-tab]');
    if (tab) {
      e.preventDefault();
      setActiveTab(tab.getAttribute('data-quick-account-tab'), true);
      return;
    }

    /* Cross-tab nudge ("Sign in or join →" inside wishlist footer
     * → switch to Enclave tab + optionally pre-select an auth pane). */
    var gotoTab = e.target.closest('[data-quick-account-goto-tab]');
    if (gotoTab) {
      e.preventDefault();
      setActiveTab(gotoTab.getAttribute('data-quick-account-goto-tab'), true);
      var paneTarget = gotoTab.getAttribute('data-quick-account-goto-auth-pane');
      if (paneTarget) setAuthPane(paneTarget);
      return;
    }

    /* Auth pane toggle */
    var authSwitch = e.target.closest('[data-quick-account-auth-switch]');
    if (authSwitch) {
      e.preventDefault();
      setAuthPane(authSwitch.getAttribute('data-quick-account-auth-switch'));
      return;
    }

    /* Wishlist actions */
    var addBtn = e.target.closest('[data-quick-account-add-to-cart]');
    if (addBtn) {
      var card1 = addBtn.closest('[data-pid]');
      if (card1) addToCart(card1);
      return;
    }
    var removeBtn = e.target.closest('[data-quick-account-remove]');
    if (removeBtn) {
      var card2 = removeBtn.closest('[data-pid]');
      if (card2) removeFromWishlist(card2);
      return;
    }

    /* Empty-state CTA click — track */
    var emptyCta = e.target.closest('[data-quick-account-empty-cta]');
    if (emptyCta) {
      track('account_drawer_empty_cta_click', { tab: currentTab, is_logged_in: isLoggedIn });
      /* let navigation proceed */
    }

    /* Generic in-drawer link tracking */
    var link = e.target.closest('.quick-account__body a[href]');
    if (link) {
      clickedLinkInSession = true;
      track('account_drawer_link_click', {
        label: (link.textContent || '').trim().slice(0, 80),
        href: link.getAttribute('href') || '',
        tab: currentTab,
        is_logged_in: isLoggedIn
      });
      pendingCloseSource = 'link_navigation';
    }
  });

  /* ---- Customer auth instrumentation -----------------------------
   *
   * Shopify customer_login + customer_register forms POST natively
   * (no AJAX), so we capture the intent at submit-time and rely on
   * the resulting page-nav with the customer cookie set to confirm
   * success. We fire the event BEFORE the form actually submits —
   * the cookie won't be in scope yet, but the cookie HEADER on the
   * follow-up page load is what GA4 / Klaviyo will use to attribute
   * sessions. Sample rates: ~95 % of submits should resolve to a
   * landed session within ~3 s.
   *
   * To deduplicate accidental double-submits, we mark the form with
   * `data-rastah-auth-tracked` after the first event and skip on
   * subsequent submit events from the same form.
   * --------------------------------------------------------------- */
  modal.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.getAttribute('data-rastah-auth-tracked') === '1') return;

    var action = String(form.action || '');
    var emailEl = form.querySelector('input[type="email"]');
    var email = emailEl ? emailEl.value : '';
    // Hashed email lets us send an identifier without exposing PII
    // in dataLayer. SHA-256 isn't built-in, so use a coarse-but-
    // deterministic identifier: lowercase the email and grab the
    // domain (no full address). Useful for spam-detection cohorts.
    var emailDomain = email.indexOf('@') > 0 ? email.split('@')[1].toLowerCase() : '';

    if (/\/account\/login/.test(action)) {
      form.setAttribute('data-rastah-auth-tracked', '1');
      track('customer_login', {
        method: 'email_password',
        surface: 'quick_account_drawer',
        email_domain: emailDomain,
        from_tab: currentTab,
        drawer_dwell_ms: openedAt ? Date.now() - openedAt : null
      });
    } else if (/\/account(?:\/|$|\?)/.test(action) && form.querySelector('input[name="customer[first_name]"]')) {
      // The customer_register form submits to /account (Shopify
      // routes that to the create endpoint based on POST verb).
      // First-name presence disambiguates from /account/login which
      // also posts to /account on certain themes.
      form.setAttribute('data-rastah-auth-tracked', '1');
      track('customer_signup', {
        method: 'email_password',
        surface: 'quick_account_drawer',
        email_domain: emailDomain,
        from_tab: currentTab,
        drawer_dwell_ms: openedAt ? Date.now() - openedAt : null,
        tier_at_signup: 'Bronze'
      });
    }
  }, true); // capture-phase so we fire before the form's native handler

  if (scrim) scrim.addEventListener('click', function () { close('scrim'); });
  if (closeBtn) closeBtn.addEventListener('click', function () { close('x_button'); });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.classList.contains('quick-account--open')) close('esc');
  });

  /* Re-hydrate wishlist if WL+ fires its own updated event while open */
  ['swym:wishlist:added', 'swym:wishlist:removed', 'swym:wishlist:updated'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      if (modal.classList.contains('quick-account--open') && currentTab === 'wishlist') {
        wishlistHydrated = false;
        hydrateWishlist();
      }
    });
  });

  /* ---- Wishlist size swap (mirrors wishlist-account.js) ----------
   *
   * Guests and logged-in-with-no-account-hub-open users both interact
   * with the drawer wishlist tab, so the size-swap affordance lives
   * here too (was only on the /account dashboard before). Patterns:
   *
   *   - Variants fetched lazily via /products/{handle}.js on first
   *     interaction with a card. Cached on `_variantCache` so a
   *     re-tap doesn't re-fetch.
   *   - Picker is an absolute-positioned popover injected as a sibling
   *     to the size button. Only one open at a time.
   *   - On select, mutate the localStorage entry in place (epi + va)
   *     and update the visible DOM without re-rendering the full grid.
   *   - Swym sync is best-effort (remove old + add new) so the
   *     server-side wishlist eventually reflects the swap. Silent
   *     failure is acceptable — the local mirror is the source of
   *     truth for the UI.
   * ---------------------------------------------------------------- */
  var _variantCache = {};
  function readLocalWl() {
    try {
      var raw = window.localStorage.getItem('rastah_wishlist_items_v1');
      var p = raw ? JSON.parse(raw) : [];
      return Array.isArray(p) ? p : [];
    } catch (e) { return []; }
  }
  function writeLocalWl(items) {
    try { window.localStorage.setItem('rastah_wishlist_items_v1', JSON.stringify(items || [])); }
    catch (e) {}
  }
  function fetchProductVariants(productUrl) {
    var handleMatch = String(productUrl || '').match(/\/products\/([^/?#]+)/);
    if (!handleMatch) return Promise.resolve([]);
    var handle = handleMatch[1];
    if (_variantCache[handle]) return Promise.resolve(_variantCache[handle]);
    return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        _variantCache[handle] = (p && p.variants) || [];
        return _variantCache[handle];
      })
      .catch(function () { return []; });
  }
  function closeAllSizePickersQA() {
    document.querySelectorAll('.quick-account__wl-size-picker').forEach(function (el) { el.remove(); });
    document.querySelectorAll('[data-quick-account-size][aria-expanded="true"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
    });
  }
  function openSizePickerQA(btn) {
    closeAllSizePickersQA();
    var card = btn.closest('.quick-account__wl-item');
    if (!card) return;
    var vid = parseInt(card.getAttribute('data-vid'), 10);
    var productUrl = btn.getAttribute('data-product-url') || '';
    btn.setAttribute('aria-expanded', 'true');
    var popover = document.createElement('div');
    // Reuse the dashboard picker class so the visual treatment is
    // identical (background, border, hairline shadow, hover) — plus
    // a drawer-scoped modifier in case it needs nudging later.
    popover.className = 'account__wishlist-card-size-picker quick-account__wl-size-picker';
    popover.innerHTML = '<div class="account__wishlist-card-size-picker-loading">Loading…</div>';
    btn.parentNode.appendChild(popover);
    fetchProductVariants(productUrl).then(function (variants) {
      if (!variants.length) {
        popover.innerHTML = '<div class="account__wishlist-card-size-picker-loading">No variants found</div>';
        return;
      }
      popover.innerHTML = variants.map(function (v) {
        var sizeLabel = v.option1 || v.title || ('Variant ' + v.id);
        var isCurrent = v.id === vid;
        var disabled = !v.available;
        return '<button type="button" class="account__wishlist-card-size-option' +
          (isCurrent ? ' account__wishlist-card-size-option--current' : '') +
          (disabled ? ' account__wishlist-card-size-option--unavailable' : '') +
          '" data-variant-id="' + v.id + '" data-variant-label="' + escape(sizeLabel) + '"' +
          (disabled ? ' disabled' : '') + '>' +
          escape(sizeLabel) +
          (disabled ? ' <span class="account__wishlist-card-size-soldout">Sold out</span>' : '') +
          '</button>';
      }).join('');
    });
  }
  function selectVariantQA(card, newVid, newLabel) {
    var pid = parseInt(card.getAttribute('data-pid'), 10);
    var oldVid = parseInt(card.getAttribute('data-vid'), 10);
    if (newVid === oldVid) { closeAllSizePickersQA(); return; }
    var items = readLocalWl();
    var idx = items.findIndex(function (it) { return it.empi === pid && it.epi === oldVid; });
    if (idx === -1) { closeAllSizePickersQA(); return; }
    var oldLabel = items[idx].va || '';
    var entryRef = items[idx]; // snapshot for analytics before mutate
    items[idx].epi = newVid;
    items[idx].va = newLabel;
    writeLocalWl(items);
    // In-place DOM mutation (no full re-render — preserves any other
    // open card state in the grid).
    card.setAttribute('data-vid', String(newVid));
    var labelEl = card.querySelector('[data-quick-account-size-label]');
    if (labelEl) labelEl.textContent = newLabel;
    // Re-sync wishlistItems cache in this module so subsequent
    // operations (move to bag, remove) use the new variant.
    var cacheIdx = wishlistItems.findIndex(function (it) { return it.empi === pid && it.epi === oldVid; });
    if (cacheIdx !== -1) {
      wishlistItems[cacheIdx].epi = newVid;
      wishlistItems[cacheIdx].va = newLabel;
    }
    closeAllSizePickersQA();
    // Analytics — size-swap is a high-intent micro-action that
    // signals "I'm tweaking before committing to add to cart".
    // Useful for cohorting: size-swap → ATC conversion vs raw
    // wishlist → ATC. Same payload shape as wishlist_to_cart_click.
    track('wishlist_size_swap', {
      product_id: pid,
      old_variant_id: oldVid,
      new_variant_id: newVid,
      old_variant_label: oldLabel,
      new_variant_label: newLabel,
      product_title: entryRef.dt || '',
      surface: 'drawer'
    });
    // Notify other surfaces (account dashboard wishlist tile if open
    // in another tab, the heart fill state on the PDP).
    document.dispatchEvent(new CustomEvent('rastah:wishlist:local-updated', {
      detail: { count: items.length, source: 'drawer_size_swap' }
    }));
    // Best-effort Swym sync: remove old + add new variant.
    var swat = swatRef || window._swat;
    if (swat && typeof swat.removeFromWishList === 'function') {
      try {
        var entry = items[idx];
        swat.removeFromWishList({ empi: pid, epi: oldVid }, function () {
          swat.addToWishList({ empi: pid, epi: newVid, du: entry.du, iu: entry.iu, dt: entry.dt, pr: entry.pr }, function () {}, function () {});
        }, function () {});
      } catch (_) {}
    }
  }
  // Delegated click handler — scoped to clicks inside the drawer's
  // wishlist panel. We don't use document-level delegation here so
  // the picker doesn't fight with the /account dashboard's own
  // handler when the user opens the drawer on top of the dashboard.
  modal.addEventListener('click', function (e) {
    var sizeOpt = e.target.closest && e.target.closest('.quick-account__wl-size-picker [data-variant-id]');
    var sizeBtn = e.target.closest && e.target.closest('[data-quick-account-size]');
    if (sizeOpt) {
      e.preventDefault();
      e.stopPropagation();
      var card = sizeOpt.closest('.quick-account__wl-item');
      if (card) selectVariantQA(card, parseInt(sizeOpt.getAttribute('data-variant-id'), 10), sizeOpt.getAttribute('data-variant-label') || '');
      return;
    }
    if (sizeBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (sizeBtn.getAttribute('aria-expanded') === 'true') closeAllSizePickersQA();
      else openSizePickerQA(sizeBtn);
      return;
    }
    // Click anywhere else inside the drawer → close any open picker.
    if (!e.target.closest('.quick-account__wl-size-picker')) closeAllSizePickersQA();
  });
  // Close the picker if the drawer closes mid-interaction.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAllSizePickersQA();
  });

  /* Public API for other surfaces to open the drawer programmatically */
  window.RastahQuickAccount = {
    open: function (source) { open(source || 'programmatic'); },
    close: function () { close('programmatic'); },
    setTab: function (tab) { setActiveTab(tab, false); }
  };
})();
